import React, { useEffect, useRef, useState } from 'react';
import {
  Compass, FileText, Layers, Users, GitBranch, CheckCircle2, Brain,
} from 'lucide-react';
import { G } from './Reveal';

const architectureStages = [
  { n: '01', title: 'Discover', desc: 'Business need and constraints.', icon: Compass },
  { n: '02', title: 'Define', desc: 'Structured brief, assumptions, scope and acceptance criteria.', icon: FileText },
  { n: '03', title: 'Decompose', desc: 'Break the requirement into capabilities, work packages and dependencies.', icon: Layers },
  { n: '04', title: 'Assemble', desc: 'Identify suitable internal or external professionals and form the appropriate team.', icon: Users },
  { n: '05', title: 'Govern', desc: 'Milestones, ownership, decisions, changes, risks and evidence.', icon: GitBranch },
  { n: '06', title: 'Accept', desc: 'Customer or accountable lead reviews delivery against agreed criteria.', icon: CheckCircle2 },
  { n: '07', title: 'Learn', desc: 'Relevant project learning feeds future scoping, delivery and team decisions.', icon: Brain },
];

const StageNode = ({ stage, on, onSelect, index }) => {
  const Icon = stage.icon;
  return (
    <button
      onMouseEnter={onSelect}
      onFocus={onSelect}
      onClick={onSelect}
      className="group flex flex-col items-center text-center focus:outline-none"
      data-testid={`arch-stage-${index}`}
    >
      <span
        className="relative z-10 w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300"
        style={{
          background: on ? G : '#141414',
          border: `1px solid ${on ? G : 'rgba(255,255,255,0.10)'}`,
          transform: on ? 'translateY(-4px)' : 'none',
          boxShadow: on ? `0 10px 30px -8px ${G}66` : 'none',
        }}
      >
        <Icon className="w-6 h-6 transition-colors" style={{ color: on ? '#0a0a0a' : '#a3a3a3' }} />
      </span>
      <span className="mt-4 text-[11px] font-mono" style={{ color: on ? G : '#525252' }}>{stage.n}</span>
      <span className={`mt-1 font-display font-bold text-sm ${on ? 'text-white' : 'text-neutral-400'} transition-colors`}>{stage.title}</span>
    </button>
  );
};

export const ArchitectureFlow = () => {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setDrawn(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const activeStage = architectureStages[active];
  const ActiveIcon = activeStage.icon;

  return (
    <div ref={trackRef}>
      <div className="relative">
        <div className="hidden lg:block absolute left-0 right-0 top-7 h-px bg-white/10" />
        <div
          className="hidden lg:block absolute left-0 top-7 h-[2px] origin-left transition-transform [transition-duration:1400ms] ease-out"
          style={{ background: G, width: '100%', transform: drawn ? 'scaleX(1)' : 'scaleX(0)' }}
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-y-8 gap-x-3 relative">
          {architectureStages.map((s, i) => (
            <StageNode key={s.title} stage={s} index={i} on={active === i} onSelect={() => setActive(i)} />
          ))}
        </div>
      </div>

      <div className="mt-10 rounded-2xl border border-white/10 bg-[#111] p-6 sm:p-8 flex items-start gap-5" key={active}>
        <span className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${G}1a`, border: `1px solid ${G}55` }}>
          <ActiveIcon className="w-5 h-5" style={{ color: G }} />
        </span>
        <div className="cb-reveal cb-in">
          <h3 className="font-display font-bold text-white text-lg">
            <span style={{ color: G }}>{activeStage.n}</span> · {activeStage.title}
          </h3>
          <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed max-w-2xl">{activeStage.desc}</p>
        </div>
      </div>
    </div>
  );
};

export default ArchitectureFlow;
